import React, {useMemo, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {
    SubscriptionRow,
} from 'src/modules/profile/account/tabs/account-profile/product-access-summary-section/subscription-row';
import {
    useAccountProductSeatUnassignmentTerms,
} from 'src/modules/profile/user/hooks/use-account-product-seat-unassignment-terms';
import {ProductSubscriptionLifecycleStatus} from 'src/openapi/admin/models/ProductSubscriptionLifecycleStatus';
import {getError} from 'src/utils/get-error';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {pluralize} from '@pb/kit-formatters';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {UnassignProductSeatRequest} from 'src/openapi/admin/models/UnassignProductSeatRequest';

const PAGE_SIZE = 5;
type Props = {
    accountId: number,
    userIds: number[],
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<UnassignProductSeatRequest>,
};

export const UnassignProductSeatModal = ({accountId, userIds, onCloseModal, processAction}: Props) => {
    const {terms, error} = useAccountProductSeatUnassignmentTerms(accountId);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const sortedTerms = useMemo(() => (terms || []).slice().sort((left, right) => {
        const statusOrder = (left.lifecycleStatus === ProductSubscriptionLifecycleStatus.ACTIVE ? 0 : 1)
            - (right.lifecycleStatus === ProductSubscriptionLifecycleStatus.ACTIVE ? 0 : 1);
        return statusOrder || left.product.name.localeCompare(right.product.name);
    }), [terms]);
    const currentPage = Math.min(page, Math.max(1, Math.ceil(sortedTerms.length / PAGE_SIZE)));
    const visibleTerms = sortedTerms.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const selectedTermIds = sortedTerms
        .filter(term => selectedIds.includes(term.productSubscriptionId))
        .map(term => term.productSubscriptionId);
    const canSubmit = !error && userIds.length > 0 && selectedTermIds.length > 0;

    const toggleTerm = (termId: string) => {
        setSelectedIds(ids => ids.includes(termId) ? ids.filter(id => id !== termId) : [...ids, termId]);
    };

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>Unassign Product Seat</DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v" gap="m">
                    <div>{pluralize('Unassign selected user[s] from each selected Product Access Term', userIds.length)}</div>
                    {error ? <Message color="error">{getError({error, separator: '. '})}</Message> : null}
                    <BoxLoading loading={terms === null}>
                        <Group direction="v" gap="s">
                            {visibleTerms.map(term => (
                                <Group key={term.productSubscriptionId} alignItems="center">
                                    <input
                                        type="checkbox"
                                        aria-label={`${term.product.name} (${term.externalSubscriptionId})`}
                                        checked={selectedIds.includes(term.productSubscriptionId)}
                                        onChange={() => toggleTerm(term.productSubscriptionId)}
                                    />
                                    <Item grow={1}><SubscriptionRow subscription={term}/></Item>
                                </Group>
                            ))}
                            {!terms?.length && !error ? (
                                <span>No Product Access Terms with assigned seats are available</span>
                            ) : null}
                        </Group>
                        <div style={{display: 'flex', justifyContent: 'end', marginTop: 16}}>
                            <PaginationStripe
                                goToPage={false}
                                pageSize={PAGE_SIZE}
                                showSizeSwitcher={false}
                                totalCount={sortedTerms.length}
                                currentPage={currentPage}
                                onPaginationChange={pagination => setPage(pagination.currentPage)}
                                sizeSwitcherPosition="RIGHT"
                                sizeSwitcherType="NONE"
                            />
                        </div>
                    </BoxLoading>
                </Group>
            </DialogMain>
            <BulkActionFooter
                action="Unassign"
                onConfirm={async (options?: ActionOptions) => {
                    if (canSubmit) {
                        return await processAction({productAccessTermIds: selectedTermIds, userIds}, options);
                    }
                }}
                disabled={!selectedIds.length}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>Cancel</Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
